/**
 * NotificationDropdown Component
 *
 * Dropdown menu for notifications:
 * - Loads notifications on open
 * - Shows loading/error/empty states
 * - Displays list of notifications
 * - Handles click on notification (mark as read + navigate)
 * - "Mark all as read" action
 */

'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/src/shared/lib/utils';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../lib/notifications.api';
import { NotificationItem } from './NotificationItem';
import { EmptyNotifications } from './EmptyNotifications';
import type { NotificationItem as NotificationItemType } from '../types';

interface NotificationDropdownProps {
  isOpen: boolean;
  locale: string;
  onClose: () => void;
  onMarkAllAsRead: () => void;
}

export function NotificationDropdown({
  isOpen,
  locale,
  onClose,
  onMarkAllAsRead,
}: NotificationDropdownProps) {
  const t = useTranslations('notifications.dropdown');
  const router = useRouter();

  const [notifications, setNotifications] = useState<NotificationItemType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    setError(null);

    getNotifications({ limit: 10, excludeTypes: ['MESSAGE_RECEIVED'] })
      .then((response) => {
        setNotifications(response.data);
      })
      .catch((err) => {
        console.error('[NotificationDropdown] Failed to load notifications:', err);
        setError(err.message || 'Failed to load notifications');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isOpen]);

  // Handle click on notification
  const handleNotificationClick = async (notification: NotificationItemType) => {
    try {
      // Mark as read if unread
      if (!notification.isRead) {
        await markNotificationAsRead(notification.id);

        // Update local state
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))
        );
      }

      // Navigate to action URL if present
      if (notification.actionUrl) {
        router.push(notification.actionUrl);
      }

      // Close dropdown
      onClose();
    } catch (err) {
      console.error('[NotificationDropdown] Failed to mark as read:', err);
      toast.error(t('error'));
    }
  };

  // Handle "Mark all as read"
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();

      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

      // Notify parent to update unread count
      onMarkAllAsRead();

      toast.success(t('allMarkedAsRead'));
    } catch (err) {
      console.error('[NotificationDropdown] Failed to mark all as read:', err);
      toast.error(t('error'));
    }
  };

  // Retry loading notifications
  const handleRetry = () => {
    setIsLoading(true);
    setError(null);

    getNotifications({ limit: 10, excludeTypes: ['MESSAGE_RECEIVED'] })
      .then((response) => {
        setNotifications(response.data);
      })
      .catch((err) => {
        console.error('[NotificationDropdown] Failed to load notifications:', err);
        setError(err.message || 'Failed to load notifications');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!isOpen) return null;

  const hasUnread = notifications.some((n) => !n.isRead);

  return (
    <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-border-primary z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary">
        <h3 className="font-semibold text-text-primary">{t('title')}</h3>
        {hasUnread && !isLoading && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
            type="button"
          >
            {t('markAllAsRead')}
          </button>
        )}
      </div>

      {/* Body */}
      <div className="max-h-96 overflow-y-auto">
        {/* Loading state */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            <p className="text-sm text-text-secondary mt-3">{t('loading')}</p>
          </div>
        )}

        {/* Error state */}
        {!isLoading && error && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <AlertCircle className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-sm text-text-primary font-medium text-center">{t('error')}</p>
            <button
              onClick={handleRetry}
              className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              type="button"
            >
              {t('retry')}
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && notifications.length === 0 && (
          <EmptyNotifications locale={locale} />
        )}

        {/* Notifications list */}
        {!isLoading && !error && notifications.length > 0 && (
          <div>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                locale={locale}
                onClick={handleNotificationClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
